import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingUp, Users, DollarSign, ChevronRight, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileCRMPage() {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [oppRes, custRes] = await Promise.all([
        axios.get(`${API}/crm/opportunities`).catch(() => ({ data: [] })),
        axios.get(`${API}/crm/customers`).catch(() => ({ data: [] })),
      ]);
      setOpportunities(oppRes.data || []);
      setCustomers(custRes.data || []);
    } catch (err) {
      console.error('CRM fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPipeline = opportunities.reduce((sum, o) => sum + (o.revenue || o.value || 0), 0);

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate('/mobile/more')}>
        <ArrowLeft size={18} /> More
      </button>

      <div className="mobile-page-title" style={{ marginBottom: 16 }}>CRM</div>

      {/* KPIs */}
      <div className="mobile-kpi-row">
        <div className="mobile-kpi-card blue">
          <div className="mobile-kpi-label">Opportunities</div>
          <div className="mobile-kpi-value">{opportunities.length}</div>
        </div>
        <div className="mobile-kpi-card green">
          <div className="mobile-kpi-label">Pipeline</div>
          <div className="mobile-kpi-value">${(totalPipeline / 1000).toFixed(0)}k</div>
        </div>
        <div className="mobile-kpi-card purple">
          <div className="mobile-kpi-label">Customers</div>
          <div className="mobile-kpi-value">{customers.length}</div>
        </div>
        <div className="mobile-kpi-card orange">
          <div className="mobile-kpi-label">Active</div>
          <div className="mobile-kpi-value">{customers.filter(c => c.status === 'active' || c.status === 'Active').length}</div>
        </div>
      </div>

      {/* Recent Opportunities */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Opportunities</span>
        <button className="mobile-section-action" onClick={() => navigate('/crm/opportunities')}>View All</button>
      </div>

      {opportunities.length === 0 ? (
        <div className="mobile-empty-state">
          <Briefcase />
          <p>No opportunities found</p>
        </div>
      ) : (
        <div className="mobile-menu-list" style={{ marginBottom: 20 }}>
          {opportunities.slice(0, 5).map((opp) => (
            <button
              key={opp.opportunity_id || opp.id}
              className="mobile-menu-item"
              onClick={() => navigate(`/mobile/crm/opportunities/${opp.opportunity_id || opp.id}`)}
            >
              <div className="mobile-menu-item-icon" style={{ background: '#007aff' }}>
                <TrendingUp size={16} />
              </div>
              <div className="mobile-menu-item-content">
                <div className="mobile-menu-item-title">{opp.customer_name || opp.name}</div>
                <div className="mobile-menu-item-subtitle">
                  {opp.stage || 'N/A'} · ${((opp.revenue || opp.value || 0) / 1000).toFixed(0)}k
                </div>
              </div>
              <ChevronRight size={16} className="mobile-menu-item-chevron" />
            </button>
          ))}
        </div>
      )}

      {/* Customers */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Customers</span>
        <button className="mobile-section-action" onClick={() => navigate('/crm/customers')}>View All</button>
      </div>

      {customers.length === 0 ? (
        <div className="mobile-empty-state">
          <Users />
          <p>No customers found</p>
        </div>
      ) : (
        <div className="mobile-menu-list">
          {customers.slice(0, 5).map((cust) => (
            <button
              key={cust.customer_id || cust.id}
              className="mobile-menu-item"
              onClick={() => navigate(`/mobile/crm/customers/${cust.customer_id || cust.id}`)}
            >
              <div className="mobile-menu-item-icon" style={{ background: '#34c759' }}>
                <Users size={16} />
              </div>
              <div className="mobile-menu-item-content">
                <div className="mobile-menu-item-title">{cust.company_name || cust.name}</div>
                <div className="mobile-menu-item-subtitle">{cust.industry || 'N/A'} · {cust.status || 'Active'}</div>
              </div>
              <ChevronRight size={16} className="mobile-menu-item-chevron" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
